import React from 'react';
import './LoadingSpinner.css';

interface LoadingSpinnerProps {
  message?: string;
  ariaLabel?: string;
  /**
   * When set, the status region is announced when shown (e.g. full-page portal validation).
   */
  ariaLive?: 'polite' | 'assertive';
  /**
   * page — full viewport height, centered (route gates, Suspense).
   * embedded — centered in parent only (forms, overlays); no min-h-screen.
   * inline — compact for cards/sections.
   */
  layout?: 'page' | 'embedded' | 'inline';
}

const bounceDots = (
  <div className="flex gap-2" aria-hidden="true">
    <div className="loading-spinner-bounce-dot w-3 h-3 rounded-full animate-bounce [animation-delay:theme(animationDelay.bounce-1)] motion-reduce:animate-none motion-reduce:opacity-100" />
    <div className="loading-spinner-bounce-dot w-3 h-3 rounded-full animate-bounce [animation-delay:theme(animationDelay.bounce-2)] motion-reduce:animate-none motion-reduce:opacity-100" />
    <div className="loading-spinner-bounce-dot w-3 h-3 rounded-full animate-bounce motion-reduce:animate-none motion-reduce:opacity-100" />
  </div>
);

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  message,
  ariaLabel,
  ariaLive,
  layout = 'page',
}) => {
  const resolvedMessage = message === undefined ? '' : message;
  const resolvedAriaLabel = ariaLabel ?? (resolvedMessage || 'Loading');
  const showCaption = Boolean(resolvedMessage);

  if (layout === 'inline') {
    return (
      <div
        className="flex flex-col items-center justify-center py-6"
        role="status"
        aria-label={resolvedAriaLabel}
        aria-live={ariaLive}
      >
        {bounceDots}
        {showCaption ? (
          <p className="m-0 mt-3 text-center text-sm font-medium text-text-secondary">
            {resolvedMessage}
          </p>
        ) : null}
      </div>
    );
  }

  if (layout === 'embedded') {
    return (
      <div
        className="flex w-full flex-col items-center justify-center py-8"
        role="status"
        aria-label={resolvedAriaLabel}
        aria-live={ariaLive}
      >
        <div className={showCaption ? 'mb-4' : ''} aria-hidden="true">
          {bounceDots}
        </div>
        {showCaption ? (
          <p className="m-0 text-center text-base font-medium text-text-secondary md:text-body-md sm:text-sm">
            {resolvedMessage}
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div
      className="flex min-h-0 w-full flex-1 flex-col items-center justify-center px-8 py-8"
      role="status"
      aria-label={resolvedAriaLabel}
      aria-live={ariaLive}
    >
      <div className={showCaption ? 'mb-6' : ''} aria-hidden="true">
        {bounceDots}
      </div>
      {showCaption ? (
        <p className="m-0 text-center text-base font-medium text-text-secondary md:text-body-md sm:text-sm">
          {resolvedMessage}
        </p>
      ) : null}
    </div>
  );
};

